import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { FileDown, Loader2, FileText, Clock, Files, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { downloadEvidenceBundle, type EvidenceBundleData } from '@/lib/export/evidenceBundleExport';
import { usePayerStatusHistory } from '@/hooks/usePayerStatusHistory';
import type { AuthorizationRequest, CaseDocument, CaseFollowUp, ActivityLogEntry } from '@/lib/authorization/types';

interface EvidenceBundleExportButtonProps {
  caseData: AuthorizationRequest;
  documents: CaseDocument[];
  followUps: CaseFollowUp[];
  activityLog: ActivityLogEntry[];
  overrideInfo?: {
    reason: string;
    overriddenBy: string;
    overriddenAt: string;
  };
  disabled?: boolean;
}

export function EvidenceBundleExportButton({
  caseData,
  documents,
  followUps,
  activityLog,
  overrideInfo,
  disabled = false,
}: EvidenceBundleExportButtonProps) {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [includeTimeline, setIncludeTimeline] = useState(true);
  const [includeDocLinks, setIncludeDocLinks] = useState(true);
  
  const { history: statusHistory, loading: loadingHistory } = usePayerStatusHistory({
    authorizationId: caseData?.id || null,
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const bundleData: EvidenceBundleData = {
        caseData,
        documents,
        followUps,
        activityLog,
        statusHistory,
        overrideInfo,
      };

      downloadEvidenceBundle(bundleData);
      
      toast.success('Evidence bundle exported successfully', {
        description: `Case ${caseData.case_number} exported as PDF`,
      });
      setOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export evidence bundle', {
        description: 'Please try again or contact support.',
      });
    } finally {
      setExporting(false);
    }
  };

  const uploadedDocsCount = documents.filter(d => d.is_uploaded).length;
  const requiredDocsCount = documents.filter(d => d.is_required).length;
  const requiredCompletedCount = documents.filter(d => d.is_required && d.is_uploaded).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || !caseData}
          className="gap-2"
        >
          <FileDown className="w-4 h-4" />
          Export Bundle
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5 text-primary" />
            Export Evidence Bundle
          </DialogTitle>
          <DialogDescription>
            Generate a comprehensive PDF export of case {caseData?.case_number} for compliance or payer escalation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="font-medium">{uploadedDocsCount} Documents</p>
                <p className="text-xs text-muted-foreground">
                  {requiredCompletedCount}/{requiredDocsCount} required
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="font-medium">{activityLog.length} Activities</p>
                <p className="text-xs text-muted-foreground">In audit log</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
              <Files className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="font-medium">{statusHistory.length} Status Updates</p>
                <p className="text-xs text-muted-foreground">Payer timeline</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="font-medium">{caseData?.auth_number ? 'Authorized' : 'Pending'}</p>
                <p className="text-xs text-muted-foreground">
                  {caseData?.auth_number || 'No auth number'}
                </p>
              </div>
            </div>
          </div>

          {/* Override Warning */}
          {overrideInfo && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
              <p className="font-medium text-amber-600 dark:text-amber-400">
                ⚠️ This case has an override applied
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Override reason will be included in the export.
              </p>
            </div>
          )}

          {/* Export Options */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Export Options</p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include-timeline"
                checked={includeTimeline}
                onCheckedChange={(checked) => setIncludeTimeline(checked as boolean)}
              />
              <Label htmlFor="include-timeline" className="text-sm">
                Include full activity timeline & status history
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include-docs"
                checked={includeDocLinks}
                onCheckedChange={(checked) => setIncludeDocLinks(checked as boolean)}
              />
              <Label htmlFor="include-docs" className="text-sm">
                Include document attachment references
              </Label>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">PHI Minimization</p>
            <p>
              Patient identifiers are minimized to initials and internal IDs. 
              Full names and other PHI are not included in the export.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || loadingHistory}
            className="gap-2"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileDown className="w-4 h-4" />
                Download PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
